import { useEffect, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  getGetSupportTicketAttachmentQueryKey,
  getSupportTicketAttachment,
} from '@/api/control/control-plane/control-plane';

/**
 * Loads attachments through the authenticated download endpoint and returns object URLs for them,
 * keyed by attachment ID, for image thumbnails and inline images. URLs are revoked when the
 * attachments change or the component unmounts. Only pass attachments that have passed the scan.
 */
export function useSupportAttachmentUrls(orgId: string, ticketId: string, attachmentIds: string[]): Record<string, string> {
  const idsKey = attachmentIds.join('|');

  // combine is structurally shared, so this array only changes when a blob does.
  const blobs = useQueries({
    queries: attachmentIds.map((attachmentId) => ({
      queryKey: getGetSupportTicketAttachmentQueryKey(orgId, ticketId, attachmentId),
      queryFn: ({ signal }: { signal: AbortSignal }) => getSupportTicketAttachment(orgId, ticketId, attachmentId, signal),
      staleTime: Infinity,
      retry: false,
    })),
    combine: (results) => results.map((result) => result.data?.data),
  });

  const [urls, setUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    const ids = idsKey ? idsKey.split('|') : [];
    const created: Record<string, string> = {};
    blobs.forEach((blob, index) => {
      if (blob instanceof Blob && blob.size > 0 && ids[index]) {
        created[ids[index]] = URL.createObjectURL(blob);
      }
    });
    setUrls(created);
    return () => {
      Object.values(created).forEach((url) => {
        URL.revokeObjectURL(url);
      });
    };
  }, [blobs, idsKey]);

  return urls;
}
